/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useState } from 'react';
import { includes } from 'lodash';

import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import FulfillmentStatusChangeModal from '../../FulfillmentStatusChangeModal';
import { getFulfillmentStatus } from '@broadleaf/admin-components/dist/oms/utils/OrderStatusUtils';
import { queueFetch } from '@broadleaf/admin-components/dist/oms/hooks/useFufillmentState/state/fulfillmentActions';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import RmFulfillmentAction from '../../components/RmFulfillmentAction';
import { getActionStatus } from '../../../utils/RmFulfillmentUtils';
import {
  IOrderFulfillment,
  IOrderLocalState
} from '@broadleaf/admin-components/dist/types/oms';
import useContextParams from '../../hooks/useContextParams';
import fulfillment from '../../components/Fulfillment';
import { IOrderViewLocalState } from '@broadleaf/admin-components/dist/oms/components';

export interface StatusChangeProps {
  actionDefinition: IMetadataAction;
  // the order* props are used in the RmItemsOrderViewSection
  orderDispatch?: Function;
  orderMetadata?: IMetadata;
  orderState?: IOrderViewLocalState;
  // the standard props need to fallback to the order* props
  dispatch?: Function;
  metadata?: IMetadata;
  state?: IFulfillmentViewLocalState | IOrderViewLocalState;
  fulfillment?: IOrderFulfillment;
}

const StatusChange: React.FC<StatusChangeProps> = ({
  actionDefinition,
  orderDispatch,
  orderMetadata,
  orderState,
  dispatch = orderDispatch,
  // the standard props need to fallback to the order* props
  metadata = orderMetadata,
  state = orderState ||
    ({} as IFulfillmentViewLocalState | IOrderViewLocalState),
  fulfillment = state?.data as IOrderFulfillment
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { attributes = {}, label } = actionDefinition;
  const {
    allowedStatuses = [],
    fulfillmentTypeAllowedStatuses = {},
    fulfillmentType = '',
    nextStatus,
    selectAllItems: selectAll,
    readOnlyItems = false,
    selectAllItemsMessage: selectAllMessage,
    submitLabel = label,
    submitNote
  } = attributes as Record<string, any>;
  const nextStatusEnum = getFulfillmentStatus(metadata, nextStatus);
  const handleClose = () => setIsOpen(false);
  const handleSubmit = useHandleSubmit({
    actionDefinition,
    dispatch,
    metadata,
    fulfillment,
    setIsSubmitting
  });
  const allAllowedStatuses = allowedStatuses.concat(
    fulfillmentTypeAllowedStatuses[fulfillment.type]
  );
  if (
    !includes(allAllowedStatuses, fulfillment.status) ||
    (fulfillmentType !== '' && fulfillmentType !== fulfillment.type)
  ) {
    return null;
  }

  const { isDisabled, actionTooltip } = getActionStatus(
    nextStatus,
    fulfillment
  );

  return (
    <>
      <RmFulfillmentAction
        color={nextStatusEnum.color}
        disabled={state.isFetching || isDisabled}
        onClick={() => setIsOpen(true)}
        tooltip={actionTooltip}
      >
        {label}
      </RmFulfillmentAction>

      {isOpen && (
        <FulfillmentStatusChangeModal
          metadata={actionDefinition}
          fulfillment={fulfillment}
          nextStatus={nextStatus}
          onClose={handleClose}
          onSubmit={handleSubmit}
          readOnlyItems={readOnlyItems}
          selectAll={selectAll}
          selectAllMessage={selectAllMessage}
          isSubmitting={isSubmitting}
          submitLabel={submitLabel}
          submitNote={submitNote}
          title={label}
        />
      )}
    </>
  );
};

function useHandleSubmit({
  actionDefinition,
  dispatch,
  metadata,
  fulfillment,
  setIsSubmitting
}) {
  const contextParams = useContextParams(metadata, fulfillment);
  const submitEndpoint = getEndpointByType(actionDefinition, 'STATUS_CHANGE');
  return useEventCallback(
    async values => {
      setIsSubmitting(true);
      const data = {
        ...values
      };
      try {
        await request(
          {
            method: 'post',
            ...submitEndpoint,
            data
          },
          contextParams
        );
      } finally {
        setIsSubmitting(false);
        dispatch(queueFetch());
      }
    },
    [submitEndpoint, contextParams, setIsSubmitting]
  );
}

export default StatusChange;
